import { FC, useCallback, useState } from 'react';
import { Calendar, View, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import cs from 'classnames';
import {
  addDays,
  format,
  getDay,
  parse,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek
} from 'date-fns';
import { az, de, enUS, es, fr, hi, pt, ptBR, ro, ru, tr } from 'date-fns/locale';

import { useMobileDesign } from 'hooks/useMobileDesign';

import LOCALES from 'constants/locales';

import {
  CalendarEvent,
  InterviewSlotSerialized,
  SlotType
} from 'types/interviewSlots';

import { selectAppLocale } from '../../features/locale/localeSelectors';
import MonthEvent from './components/MonthEvent/MonthEvent';
import SchedulerFooter from './components/SchedulerFooter/SchedulerFooter';
import Toolbar from './components/Toolbar/Toolbar';
import {
  DAYS_AHEAD,
  MAX_HOUR,
  MIN_HOUR,
  calendarSettings,
  getCalendarFormats,
  getCalendarMessages
} from './config/calendarConfig';
import { useSchedulerEvents } from './hooks/useSchedulerEvents';
import { useSlotValidation } from './hooks/useSlotValidation';

const locales = {
  [LOCALES.ENGLISH_US]: enUS,
  [LOCALES.SPANISH_ES]: es,
  [LOCALES.SPANISH_LATAM]: es,
  [LOCALES.PORTUGUESE_BR]: ptBR,
  [LOCALES.PORTUGUESE_PT]: pt,
  [LOCALES.FRENCH_FR]: fr,
  [LOCALES.RUSSIAN_RU]: ru,
  [LOCALES.GERMAN_DE]: de,
  [LOCALES.HINDI_IN]: hi,
  [LOCALES.TURKISH_TR]: tr,
  [LOCALES.AZERBAIJANI_AZ]: az,
  [LOCALES.ROMANIAN_RO]: ro
};

const localizer = dateFnsLocalizer({
  format: (date: Date, formatStr: string) => format(date, formatStr),
  parse,
  startOfWeek,
  getDay,
  locales
});

const today = startOfDay(new Date());
const maxSelectableDate = startOfDay(addDays(today, DAYS_AHEAD));
const minDate = setMinutes(setHours(new Date(), MIN_HOUR), 0);
const maxDate = setMinutes(setHours(new Date(), MAX_HOUR), 0);

interface InterviewSchedulerProps {
  onConfirm: () => void;
  proposedSlots?: InterviewSlotSerialized[];
}

const InterviewScheduler: FC<InterviewSchedulerProps> = ({
  onConfirm,
  proposedSlots = []
}) => {
  const [view, setView] = useState<View>(calendarSettings.defaultDesktopView);
  const [date, setDate] = useState(new Date());
  const { i18n } = useTranslation();
  const language = useSelector(selectAppLocale);

  const isMobile = useMobileDesign();

  const { isSlotDisabled, dayPropGetter, slotPropGetter } = useSlotValidation({
    maxSelectableDate
  });

  const currentLocale = locales[language as keyof typeof locales] || locales[LOCALES.ENGLISH_US];

  const { events, handleSelectEvent, handleSelectSlot } = useSchedulerEvents({
    isMobile,
    maxSelectableDate,
    isSlotDisabled,
    proposedSlots
  });

  const eventPropGetter = useCallback((event: CalendarEvent) => {
    const className =
      event.type === SlotType.Proposed ? 'event-proposed' : 'event-selected';
    return {
      className
    };
  }, []);

  return (
    <>
      {/* @ts-ignore */}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        className={cs('interview-scheduler', {
          'is-mobile': isMobile,
          'hide-month-headers': view === 'month'
        })}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={(slotInfo) =>
          handleSelectSlot(slotInfo, view, setView, setDate)
        }
        selectable="ignoreEvents"
        view={isMobile ? calendarSettings.defaultMobileView : view}
        onView={isMobile ? undefined : setView}
        date={date}
        onNavigate={setDate}
        defaultView={
          isMobile
            ? calendarSettings.defaultMobileView
            : calendarSettings.defaultDesktopView
        }
        views={
          isMobile
            ? calendarSettings.mobileViews
            : calendarSettings.desktopViews
        }
        min={minDate}
        max={maxDate}
        step={calendarSettings.step}
        timeslots={calendarSettings.timeslots}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        eventPropGetter={eventPropGetter}
        scrollToTime={minDate}
        formats={getCalendarFormats(currentLocale)}
        messages={getCalendarMessages(i18n)}
        components={{
          toolbar: (toolbarProps) => (
            <Toolbar {...toolbarProps} isMobile={isMobile} />
          ),
          month: {
            event: MonthEvent
          }
        }}
        popup={false}
        longPressThreshold={
          isMobile
            ? calendarSettings.longPressThresholdMobile
            : calendarSettings.longPressThresholdDesktop
        }
        dayLayoutAlgorithm="no-overlap"
      />
      <SchedulerFooter onConfirm={onConfirm} />
    </>
  );
};

export default InterviewScheduler;
